import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, MessageCircle, Eye, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { ClawMark, Wordmark } from "../components/site/Brand";
import { BookingDetail, StatusSelect, clientWa } from "../components/admin/AdminParts";
import { BlockedDates } from "../components/admin/BlockedDates";
import { CompanySettings } from "../components/admin/CompanySettings";
import { api, apiError } from "../lib/api";
import { STATUS, brl } from "../lib/data";
import { fmtDate } from "../lib/pricing";

const Stat = ({ label, value, testid, accent }) => (
  <div className={`rounded-2xl border p-5 ${accent ? "bg-[#FFD200] text-[#08080C] border-[#FFD200]" : "border-white/10 bg-[#111219]"}`}>
    <p className={`font-mono text-[11px] uppercase tracking-[0.18em] ${accent ? "text-[#08080C]/70" : "text-zinc-400"}`}>{label}</p>
    <p data-testid={testid} className="font-display font-black text-lg sm:text-2xl md:text-3xl mt-2 break-words">{value}</p>
  </div>
);

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState("");
  const [detail, setDetail] = useState(null);
  const [ready, setReady] = useState(false);
  const [tick, setTick] = useState(0);

  const load = useCallback(async () => {
    try {
      const [b, s] = await Promise.all([api.get("/admin/bookings", { params: filter ? { status: filter } : {} }), api.get("/admin/stats")]);
      setBookings(Array.isArray(b.data) ? b.data : []);
      setStats(s.data ?? null);
      setReady(true);
    } catch (e) {
      if (e?.response?.status === 401) navigate("/admin/login");
      else toast.error(apiError(e));
    }
  }, [filter, navigate]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/bookings/${id}`, { status });
      toast.success("Status atualizado");
      load();
      setTick((t) => t + 1);
    } catch (e) { toast.error(apiError(e)); }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) { /* ignore */ }
    localStorage.removeItem("pt_admin_token");
    navigate("/admin/login");
  };

  if (!ready) return <main className="min-h-screen grid place-items-center text-zinc-400">Carregando…</main>;

  return (
    <main data-testid="admin-dashboard-container" className="min-h-screen pb-20">
      <header className="border-b border-white/10 bg-[#08080C]/80 backdrop-blur sticky top-0 z-30">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2"><ClawMark className="w-8 h-8" /><Wordmark size="text-base" /></Link>
          <div className="flex items-center gap-2">
            <button onClick={load} data-testid="admin-refresh" className="p-2 text-zinc-300 hover:text-white" aria-label="Atualizar"><RefreshCw className="w-4 h-4" /></button>
            <button onClick={logout} data-testid="admin-logout" className="inline-flex items-center gap-2 text-sm text-zinc-300 hover:text-white"><LogOut className="w-4 h-4" /> Sair</button>
          </div>
        </div>
      </header>
      <div className="max-w-[1400px] mx-auto px-5 lg:px-10 pt-10">
        <h1 className="font-display font-black uppercase text-3xl md:text-4xl text-white">Reservas</h1>
        <div className="mt-8 grid grid-cols-2 lg:grid-cols-5 gap-3">
          <Stat label="Recebido" value={brl(stats?.received)} testid="admin-stat-received" accent />
          <Stat label="A receber" value={brl(stats?.receivable)} testid="admin-stat-receivable" />
          <Stat label="Confirmadas" value={stats?.confirmed ?? 0} testid="admin-stat-confirmed" />
          <Stat label="Aguardando pgto" value={stats?.pending ?? 0} testid="admin-stat-pending" />
          <Stat label="Pelúcias" value={stats?.plushies ?? 0} testid="admin-stat-plushies" />
        </div>
        <div className="mt-10 flex flex-wrap gap-2" data-testid="admin-filters">
          {[["", "Todas"], ...Object.entries(STATUS).map(([k, v]) => [k, v.label])].map(([k, l]) => (
            <button key={k || "all"} onClick={() => setFilter(k)} data-testid={`admin-filter-${k || "all"}`}
              className={`rounded-full px-4 py-2 text-sm border transition-colors ${filter === k ? "bg-[#FFD200] text-[#08080C] border-[#FFD200]" : "border-white/15 text-zinc-300 hover:border-white/40"}`}>{l}</button>
          ))}
        </div>
        <div className="mt-6 rounded-2xl border border-white/10 overflow-x-auto" data-lenis-prevent>
          <table className="w-full text-sm min-w-[900px]">
            <thead className="bg-white/[0.03] text-left font-mono text-[11px] uppercase tracking-[0.15em] text-zinc-400">
              <tr>{["Evento", "Cliente", "Pacote", "Pagamento", "Total / pago", "Status", ""].map((h) => <th key={h} className="px-4 py-3 font-normal">{h}</th>)}</tr>
            </thead>
            <tbody>
              {bookings.length === 0 && <tr><td colSpan={7} className="px-4 py-12 text-center text-zinc-400" data-testid="admin-empty">Nenhuma reserva por aqui ainda.</td></tr>}
              {bookings.map((b) => (
                <tr key={b.id} data-testid="admin-booking-row" className="border-t border-white/5 hover:bg-white/[0.02]">
                  <td className="px-4 py-4"><p className="text-white font-semibold">{fmtDate(b.event_date)} · {b.event_time}</p><p className="text-zinc-400">{b.city}</p></td>
                  <td className="px-4 py-4"><p className="text-white">{b.client_name}</p><p className="text-zinc-400">{b.client_phone}</p></td>
                  <td className="px-4 py-4 text-zinc-200">{b.package_name}{b.extra_plushies ? ` + ${b.extra_plushies}` : ""}</td>
                  <td className="px-4 py-4 text-zinc-200">{b.payment_option === "full" ? (b.payment_method === "pagarme" ? "Integral" : "Integral 5% off") : "Sinal 30%"}<p className="text-zinc-400">{b.payment_method === "pagarme" ? "Pagar.me · parcelado" : "Stripe"}</p></td>
                  <td className="px-4 py-4"><p className="text-white">{brl(b.total)}</p><p className="text-zinc-400">pago {brl(b.amount_paid)}</p></td>
                  <td className="px-4 py-4"><StatusSelect value={b.status} onChange={(v) => updateStatus(b.id, v)} testid={`admin-status-select-${b.id}`} /></td>
                  <td className="px-4 py-4">
                    <div className="flex gap-1 justify-end">
                      <button onClick={() => setDetail(b)} data-testid={`admin-view-${b.id}`} className="p-2 rounded-full hover:bg-white/10 text-zinc-200" aria-label="Detalhes"><Eye className="w-4 h-4" /></button>
                      <a href={clientWa(b)} target="_blank" rel="noreferrer" data-testid={`admin-wa-${b.id}`} className="p-2 rounded-full hover:bg-white/10 text-[#25D366]" aria-label="WhatsApp"><MessageCircle className="w-4 h-4" /></a>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <BlockedDates refreshKey={tick} />
        <CompanySettings />
      </div>
      <BookingDetail booking={detail} onClose={() => setDetail(null)} />
    </main>
  );
}
